"use client";

import { useState } from "react";
import { Frame, Verdict, Note } from "./parts";

/**
 * Copier une valeur contre partager une référence.
 *
 * Deux réglages : la ligne de copie (affectation, spread, structuredClone) et
 * le champ que l'on écrit ensuite dans la copie (surface ou imbriqué). Ce qui
 * bascule, c'est l'état de l'original : intact avec { ...profil } tant qu'on
 * écrit au premier niveau, modifié dès qu'on descend d'un cran, parce que
 * l'objet adresse, lui, n'a jamais été recopié.
 *
 * Les six combinaisons ont été rejouées en vrai JavaScript avant d'écrire ce
 * composant : le modèle ci-dessous donne le même résultat sur 6/6.
 */

const L = {
    fr: {
        title: "Trois lignes qui ressemblent à une copie, et celle qui protège vraiment l'original",
        step1: "1. La ligne de copie",
        prefix: "const copie =",
        modes: {
            alias: "profil",
            shallow: "{ ...profil }",
            clone: "structuredClone(profil)",
        },
        step2: "2. Ce que vous écrivez ensuite dans la copie",
        targets: {
            nom: "copie.nom = \"Grace\"",
            ville: "copie.adresse.ville = \"Paris\"",
        },
        colOriginal: "L'original",
        colCopy: "La copie",
        rowNumber: "Un nombre : deux valeurs, aucun objet",
        rowNumberCode: "let a = 10; let b = a; b += 5;",
        rowOuter: "L'objet profil",
        rowInner: "L'objet adresse, un niveau plus bas",
        shared: "partagé",
        colon: " : ",
        objects: "Objets en mémoire",
        sharedObjects: "Objets partagés par les deux noms",
        intact: "intact",
        changed: "modifié",
        varOriginal: "profil",
        varCopy: "copie",
        fieldName: "nom",
        fieldAddress: "adresse",
        fieldCity: "ville",
        nameStart: "Ada",
        nameEnd: "Grace",
        cityStart: "Londres",
        cityEnd: "Paris",
        vAlias: "Ici, copie n'est pas une copie : c'est un second nom posé sur le même objet. Ce que vous écrivez à travers l'un, vous le relisez à travers l'autre, à n'importe quelle profondeur.",
        vShallowSurface: "Le premier niveau est bien détaché : profil.nom vaut toujours \"Ada\". Changez maintenant la cible pour copie.adresse.ville : c'est là que la protection s'arrête.",
        vShallowNested: "Voilà le piège. { ...profil } a bien fabriqué un nouvel objet, mais il y a recopié l'adresse mémoire du sous-objet adresse : les deux pointent sur le même. profil.adresse.ville vaut \"Paris\" alors que personne n'a touché à profil.",
        vClone: "structuredClone() a refabriqué la structure niveau par niveau. Plus aucun objet n'est partagé, et l'original est protégé jusqu'en bas.",
        note: "Le tableau représente la mémoire de façon simplifiée : sur les deux dernières lignes, chaque cadre plein est un objet, et un cadre à cheval sur les deux colonnes est un objet unique atteint par les deux noms. Les cadres en pointillés du haut sont des valeurs, pas des objets, et ne comptent pas. { ...profil } ne recopie que les propriétés propres et énumérables du premier niveau, et transforme les accesseurs (getters) en valeurs figées. structuredClone() a lui aussi ses limites : il refuse une fonction et rend l'objet sans son prototype, donc sans ses méthodes de classe. Le nombre, lui, n'a pas de mode de copie : une primitive est recopiée à chaque affectation, il n'y a aucune adresse à partager.",
    },
    en: {
        title: "Three lines that look like a copy, and the one that really protects the original",
        step1: "1. The copy line",
        prefix: "const copy =",
        modes: {
            alias: "profile",
            shallow: "{ ...profile }",
            clone: "structuredClone(profile)",
        },
        step2: "2. What you then write into the copy",
        targets: {
            nom: "copy.name = \"Grace\"",
            ville: "copy.address.city = \"Paris\"",
        },
        colOriginal: "The original",
        colCopy: "The copy",
        rowNumber: "A number: two values, no object",
        rowNumberCode: "let a = 10; let b = a; b += 5;",
        rowOuter: "The profile object",
        rowInner: "The address object, one level down",
        shared: "shared",
        colon: ": ",
        objects: "Objects in memory",
        sharedObjects: "Objects shared by both names",
        intact: "unchanged",
        changed: "changed",
        varOriginal: "profile",
        varCopy: "copy",
        fieldName: "name",
        fieldAddress: "address",
        fieldCity: "city",
        nameStart: "Ada",
        nameEnd: "Grace",
        cityStart: "London",
        cityEnd: "Paris",
        vAlias: "Here, copy is not a copy: it is a second name stuck on the same object. What you write through one, you read back through the other, at any depth.",
        vShallowSurface: "The first level really is detached: profile.name still reads \"Ada\". Now switch the target to copy.address.city, because that is where the protection stops.",
        vShallowNested: "Here is the trap. { ...profile } did build a new object, but it copied the memory address of the nested address object into it: both point at the same one. profile.address.city now reads \"Paris\" although nobody touched profile.",
        vClone: "structuredClone() rebuilt the structure level by level. No object is shared anymore, and the original is protected all the way down.",
        note: "The table is a simplified view of memory: on the last two rows every solid frame is one object, and a frame straddling both columns is a single object reached through both names. The dashed frames on top are values, not objects, and are left out of the count. { ...profile } only copies own enumerable properties at the first level, and freezes getters into plain values at that moment. structuredClone() has limits of its own: it refuses a function, and it returns the object without its prototype, so without its class methods. The number has no copy mode at all: a primitive is duplicated on every assignment, there is no address to share.",
    },
};

const MODES = ["alias", "shallow", "clone"];
const CIBLES = ["nom", "ville"];

export default function ReferenceVsValue({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const [mode, setMode] = useState("shallow");
    const [cible, setCible] = useState("nom");

    // Ce que la ligne de copie partage réellement.
    const partageExterne = mode === "alias";
    const partageInterne = mode === "alias" || mode === "shallow";

    const ecritNom = cible === "nom";

    // Valeurs après l'écriture, côté copie et côté original.
    const copNom = ecritNom ? t.nameEnd : t.nameStart;
    const origNom = ecritNom && partageExterne ? t.nameEnd : t.nameStart;
    const copVille = ecritNom ? t.cityStart : t.cityEnd;
    const origVille = !ecritNom && partageInterne ? t.cityEnd : t.cityStart;

    const origBouge = ecritNom ? origNom !== t.nameStart : origVille !== t.cityStart;

    // Ce que le tableau dessine : 2 cadres objets si alias, 3 si spread, 4 si clone.
    const nbObjets = (partageExterne ? 1 : 2) + (partageInterne ? 1 : 2);
    const nbPartages = (partageExterne ? 1 : 0) + (partageInterne ? 1 : 0);

    const bouton = (actif, contenu, onClick, cle) => (
        <button
            key={cle}
            type="button"
            onClick={onClick}
            aria-pressed={actif}
            className={`rounded-lg px-3 py-2 font-geistMono text-xs leading-tight transition-colors duration-200 ${
                actif
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/10 dark:text-grayDark-200 dark:hover:bg-white/20"
            }`}
        >
            {contenu}
        </button>
    );

    const champ = (nom, valeur, rouge, gras) => (
        <div className="font-geistMono text-[11px] sm:text-sm">
            {nom ? <span className="text-gray-400 dark:text-lightBlue/60">{nom}: </span> : null}
            <span
                className={
                    rouge
                        ? "font-bold text-red-600 dark:text-red-400"
                        : gras
                          ? "font-bold text-gray-900 dark:text-white"
                          : "text-gray-700 dark:text-lightBlue"
                }
            >
                {valeur}
            </span>
        </div>
    );

    const versLeBas = (
        <div className="font-geistMono text-[11px] sm:text-sm">
            <span className="text-gray-400 dark:text-lightBlue/60">{t.fieldAddress}: </span>
            <span className="text-gray-400 dark:text-lightBlue/60">↓</span>
        </div>
    );

    const cadre = (noms, partage, span, contenu, pointille) => (
        <div
            className={`${span ? "col-span-2" : ""} rounded-xl border p-2.5 sm:p-3 transition-colors duration-200 ${
                partage
                    ? "border-primary/60 bg-primary/[0.06] dark:bg-primary/10"
                    : pointille
                      ? "border-dashed border-gray-300 dark:border-white/20"
                      : "border-gray-200 bg-white/70 dark:border-white/10 dark:bg-white/[0.04]"
            }`}
        >
            <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                {noms.map((n) => (
                    <span
                        key={n}
                        className="rounded bg-gray-100 px-1.5 py-0.5 font-geistMono text-[11px] text-gray-600 dark:bg-white/10 dark:text-lightBlue"
                    >
                        {n}
                    </span>
                ))}
                {partage ? (
                    <span className="text-[10px] font-bold uppercase tracking-widest text-primary">
                        {t.shared}
                    </span>
                ) : null}
            </div>
            <div className="space-y-0.5">{contenu}</div>
        </div>
    );

    const etiquette = (texte, code) => (
        <div className="col-span-2 mt-2 flex flex-wrap items-baseline gap-x-2 text-[11px] uppercase tracking-widest text-gray-400 dark:text-lightBlue/60">
            <span>{texte}</span>
            {code ? (
                <span className="font-geistMono normal-case tracking-normal text-gray-400 dark:text-lightBlue/60">
                    {code}
                </span>
            ) : null}
        </div>
    );

    const pastille = (chemin, valeur, bouge) => (
        <div
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 transition-colors duration-200 ${
                bouge
                    ? "border-red-200 bg-red-50 dark:border-red-400/30 dark:bg-red-400/10"
                    : "border-green-200 bg-green-50 dark:border-green-400/30 dark:bg-green-400/10"
            }`}
        >
            <span className="font-geistMono text-xs text-gray-600 dark:text-lightBlue">{chemin}</span>
            <span className="font-geistMono text-xs font-bold text-gray-900 dark:text-white">
                {valeur}
            </span>
            <span
                className={`ml-auto text-[10px] font-bold uppercase tracking-widest ${
                    bouge ? "text-red-600 dark:text-red-400" : "text-green-700 dark:text-green-400"
                }`}
            >
                {bouge ? t.changed : t.intact}
            </span>
        </div>
    );

    const cheminObjet = ecritNom
        ? `${t.varOriginal}.${t.fieldName}`
        : `${t.varOriginal}.${t.fieldAddress}.${t.fieldCity}`;
    const valeurObjet = ecritNom ? origNom : origVille;

    let ton = "good";
    let verdict = t.vClone;
    if (mode === "alias") {
        ton = "bad";
        verdict = t.vAlias;
    } else if (mode === "shallow") {
        ton = ecritNom ? "warn" : "bad";
        verdict = ecritNom ? t.vShallowSurface : t.vShallowNested;
    }

    return (
        <Frame icon="link-linear" title={t.title}>
            <div className="space-y-4">
                <div>
                    <div className="mb-2 flex flex-wrap items-baseline gap-x-2 text-sm text-gray-600 dark:text-lightBlue">
                        <span>{t.step1}</span>
                        <span className="font-geistMono text-xs text-gray-400 dark:text-lightBlue/60">
                            {t.prefix}
                        </span>
                    </div>
                    <div className="grid gap-2 sm:grid-cols-3">
                        {MODES.map((m) => bouton(mode === m, t.modes[m], () => setMode(m), m))}
                    </div>
                </div>
                <div>
                    <div className="mb-2 text-sm text-gray-600 dark:text-lightBlue">{t.step2}</div>
                    <div className="grid gap-2 sm:grid-cols-2">
                        {CIBLES.map((c) => bouton(cible === c, t.targets[c], () => setCible(c), c))}
                    </div>
                </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-2.5 sm:gap-3">
                <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500 dark:text-lightBlue">
                    {t.colOriginal}
                </div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500 dark:text-lightBlue">
                    {t.colCopy}
                </div>

                {etiquette(t.rowNumber, t.rowNumberCode)}
                {cadre(["a"], false, false, champ("", "10", false, false), true)}
                {cadre(["b"], false, false, champ("", "15", false, true), true)}

                {etiquette(t.rowOuter, null)}
                {partageExterne ? (
                    cadre([t.varOriginal, t.varCopy], true, true, (
                        <>
                            {champ(t.fieldName, `"${origNom}"`, origNom !== t.nameStart, false)}
                            {versLeBas}
                        </>
                    ))
                ) : (
                    <>
                        {cadre([t.varOriginal], false, false, (
                            <>
                                {champ(t.fieldName, `"${origNom}"`, origNom !== t.nameStart, false)}
                                {versLeBas}
                            </>
                        ))}
                        {cadre([t.varCopy], false, false, (
                            <>
                                {champ(t.fieldName, `"${copNom}"`, false, copNom !== t.nameStart)}
                                {versLeBas}
                            </>
                        ))}
                    </>
                )}

                {etiquette(t.rowInner, null)}
                {partageInterne ? (
                    cadre(
                        [`${t.varOriginal}.${t.fieldAddress}`, `${t.varCopy}.${t.fieldAddress}`],
                        true,
                        true,
                        champ(t.fieldCity, `"${origVille}"`, origVille !== t.cityStart, false)
                    )
                ) : (
                    <>
                        {cadre(
                            [`${t.varOriginal}.${t.fieldAddress}`],
                            false,
                            false,
                            champ(t.fieldCity, `"${origVille}"`, origVille !== t.cityStart, false)
                        )}
                        {cadre(
                            [`${t.varCopy}.${t.fieldAddress}`],
                            false,
                            false,
                            champ(t.fieldCity, `"${copVille}"`, false, copVille !== t.cityStart)
                        )}
                    </>
                )}
            </div>

            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-500 dark:text-lightBlue/80">
                <span>
                    {t.objects}
                    {t.colon}
                    <span className="font-bold tabular-nums text-gray-900 dark:text-white">
                        {nbObjets}
                    </span>
                </span>
                <span>
                    {t.sharedObjects}
                    {t.colon}
                    <span className="font-bold tabular-nums text-primary">{nbPartages}</span>
                </span>
            </div>

            <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {pastille("a", "10", false)}
                {pastille(cheminObjet, `"${valeurObjet}"`, origBouge)}
            </div>

            <Verdict tone={ton}>{verdict}</Verdict>
            <Note>{t.note}</Note>
        </Frame>
    );
}
